import React, { useCallback, useMemo, useState } from 'react';
import {
  Grid,
  makeStyles,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import {
  AddCircle as AddCircleIcon,
  Pageview as PageviewIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  MoreHoriz as MoreHorizIcon,
  Clear as ClearIcon,
} from '@material-ui/icons';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';
import { get, isFunction, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import QCXButton from '../button/QCXButton';
import QCXDataGrid from '../data-grid/QCXDataGrid';
import QCXConfirmDialog from '../dialog/QCXConfirmDialog';
import QCXPopupState from '../../components/popup-state/QCXPopupState';
import QCXListItemIcon from '../list-item-icon/QCXListItemIcon';
import {
  setErrorFeedback,
  setSuccessFeedback,
} from '../../features/feedback/feedbackSlice';
import { isLoadingStatus } from '../../utils/store/store-utils';

const useStyles = makeStyles(() => ({
  dataGridDiv: { width: 'calc(100% - 0px) !important' },
}));

export default function QCXFinalBondManagerOldBatch({
  listProps,
  formProps = {},
  reducerConfig,
  handleAdd,
  handleUpdate,
  handleRemove,
  disableDefaultRemove = false,
  disableAdd = false,
  readOnlyMode = false,
  disableRemove = false,
  hideAddButton = false,
  children,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const form = useForm();
  const { values } = useFormState();

  // Estados para exclusão em lote
  const [batchDeleteMode, setBatchDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // Estado de edição / consulta
  const [editingId, setEditingId] = useState(null);

  const {
    selectors: { selectStatus },
    actions: { loading, resetStatus },
  } = reducerConfig;

  const status = useSelector(selectStatus);
  const isBusy = isLoadingStatus(status);

  const vinculoListName = useMemo(() => listProps?.name, [listProps]);
  const currentVinculos = useMemo(
    () => get(values, vinculoListName) || [],
    [values, vinculoListName]
  );

  const updateFormList = (newList) => form.change(vinculoListName, newList);

  const tools = useMemo(
    () => ({
      formUtils: { form, values },
    }),
    [form, values]
  );

  const currentVinculo = useMemo(
    () => get(values, formProps?.rootName),
    [values, formProps]
  );

  /* ------------------------- ADD ---------------------------- */
  const handleSuccessAddVinculo = useCallback(
    (updated) => {
      updateFormList(updated);
      form.change(formProps?.rootName, undefined);
    },
    [updateFormList, form, formProps]
  );

  const handleAddVinculo = useCallback(async () => {
    if (isBusy || disableAdd) return;

    if (!currentVinculo || isEmpty(currentVinculo)) return;

    const jaExiste = currentVinculos.some((v) => v?.id === currentVinculo?.id);
    if (jaExiste) {
      dispatch(
        setErrorFeedback({
          message: 'Já existe um vínculo com este item.',
        })
      );
      return;
    }

    try {
      dispatch(loading());

      if (isFunction(handleAdd)) {
        await handleAdd(
          currentVinculo,
          currentVinculos,
          handleSuccessAddVinculo,
          tools
        );
      } else {
        handleSuccessAddVinculo([...currentVinculos, currentVinculo]);
      }
      dispatch(
        setSuccessFeedback({
          message: 'Vínculo adicionado com sucesso',
        })
      );
    } catch (err) {
      dispatch(
        setErrorFeedback({
          message: err?.response?.data?.message || err.message || 'Erro ao adicionar',
        })
      );
    } finally {
      dispatch(resetStatus());
    }
  }, [
    currentVinculo,
    currentVinculos,
    handleAdd,
    disableAdd,
    isBusy,
    loading,
    resetStatus,
  ]);

  /* ------------------------- UPDATE ------------------------- */
  const handleSuccessUpdateVinculo = useCallback(
    (updated) => {
      updateFormList(updated);
      form.change(formProps?.rootName, undefined);
      setEditingId(null);
    },
    [updateFormList, form, formProps]
  );

  const handleUpdateVinculo = useCallback(async () => {
    if (isBusy) return;

    if (!currentVinculo || isEmpty(currentVinculo)) return;

    try {
      dispatch(loading());

      if (isFunction(handleUpdate)) {
        await handleUpdate(
          currentVinculo,
          currentVinculos,
          handleSuccessUpdateVinculo,
          tools
        );
      } else {
        const listaSemAntigo = currentVinculos.filter((v) => v.id !== editingId);
        handleSuccessUpdateVinculo([...listaSemAntigo, currentVinculo]);
      }

      dispatch(
        setSuccessFeedback({
          message: 'Vínculo atualizado com sucesso',
        })
      );
    } catch (err) {
      dispatch(
        setErrorFeedback({
          message: err?.response?.data?.message || err.message || 'Erro ao atualizar',
        })
      );
    } finally {
      dispatch(resetStatus());
    }
  }, [
    editingId,
    currentVinculo,
    currentVinculos,
    handleUpdate,
    isBusy,
    loading,
    resetStatus,
  ]);

  /* ------------------------- CLEAR -------------------------- */
  const handleClearCurrentVinculo = useCallback(() => {
    form.change(formProps?.rootName, undefined);
    setEditingId(null);
  }, [form, formProps]);

  /* ------------------------- DELETE ------------------------- */
  const handleConfirmBatchDelete = useCallback(async () => {
    dispatch(loading());

    const idsParaRemover = [...selectedIds];
    let listaAtual = [...currentVinculos];

    try {
      if (disableDefaultRemove && isFunction(handleRemove)) {
        await idsParaRemover.reduce(
          (chain, id) =>
            chain.then(async () => {
              const row = listaAtual.find((v) => v.id === id);
              if (!row) return;

              await handleRemove({
                target: row,
                list: listaAtual,
                handleSuccess: () => {},
                tools,
              });

              listaAtual = listaAtual.filter((v) => v.id !== id);
              updateFormList(listaAtual);
            }),
          Promise.resolve()
        );
      } else {
        listaAtual = listaAtual.filter((v) => !idsParaRemover.includes(v.id));
        updateFormList(listaAtual);
      }

      dispatch(
        setSuccessFeedback({
          message: 'Exclusão realizada com sucesso',
        })
      );
    } catch (err) {
      dispatch(
        setErrorFeedback({
          message: err?.response?.data?.message || err.message || 'Erro ao remover',
        })
      );
    } finally {
      setBatchDeleteMode(false);
      setSelectedIds([]);
      dispatch(resetStatus());
    }
  }, [
    selectedIds,
    currentVinculos,
    disableDefaultRemove,
    handleRemove,
    loading,
    tools,
    resetStatus,
  ]);

  // Remoção individual
  const handleRemoveSingleVinculo = useCallback(
    async (row) => {
      try {
        dispatch(loading());

        if (disableDefaultRemove && isFunction(handleRemove)) {
          await handleRemove({
            target: row,
            list: currentVinculos,
            handleSuccess: () => {},
            tools,
          });
        }

        const listaAtual = currentVinculos.filter((v) => v.id !== row.id);
        updateFormList(listaAtual);

        if (editingId === row.id) {
          handleClearCurrentVinculo();
        }

        dispatch(
          setSuccessFeedback({
            message: 'Exclusão realizada com sucesso',
          })
        );
      } catch (err) {
        dispatch(
          setErrorFeedback({
            message: err?.response?.data?.message || err.message || 'Erro ao remover',
          })
        );
      } finally {
        dispatch(resetStatus());
      }
    },
    [
      editingId,
      currentVinculos,
      disableDefaultRemove,
      handleRemove,
      handleClearCurrentVinculo,
      loading,
      resetStatus,
      tools,
    ]
  );

  /* ------------------------- CONSULT ------------------------ */
  const handleConsultVinculo = useCallback(
    (row) => {
      form.change(formProps?.rootName, row);
      setEditingId(row.id);
    },
    [form, formProps]
  );

  /* ------------------------- COLUMNS ------------------------ */
  const actionColumn = useMemo(() => (
    readOnlyMode
      ? []
      : [
          {
            field: 'actions',
            headerName: t('com.muralis.qcx.acoes.label'),
            headerAlign: 'center',
            align: 'center',
            width: 90,
            sortable: false,
            renderCell: ({ row }) => (
              <QCXPopupState popupId={`popup-menu-batch-${row.id}`}> 
                {(popupState) => (
                  <>
                    <IconButton {...bindTrigger(popupState)}>
                      <MoreHorizIcon color="secondary" />
                    </IconButton>
                    <Menu {...bindMenu(popupState)}>
                      <MenuItem
                        onClick={(e) => {
                          popupState.close(e);
                          handleConsultVinculo(row);
                        }}
                      >
                        <QCXListItemIcon>
                          <PageviewIcon fontSize="small" />
                        </QCXListItemIcon>
                        <Typography variant="inherit" style={{ fontSize: 12 }}>
                          {t('com.muralis.qcx.acoes.consultar').toUpperCase()}
                        </Typography>
                      </MenuItem>
                      {!disableRemove && (
                        <MenuItem
                          onClick={(e) => {
                            popupState.close(e);
                            handleRemoveSingleVinculo(row);
                          }}
                        >
                          <QCXListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                          </QCXListItemIcon>
                          <Typography variant="inherit" style={{ fontSize: 12 }}>
                            {t('com.muralis.qcx.acoes.excluir').toUpperCase()}
                          </Typography>
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </QCXPopupState>
            ),
          },
        ]
  ), [readOnlyMode, disableRemove, handleConsultVinculo, handleRemoveSingleVinculo, t]);

  const columns = useMemo(
    () => (listProps?.columns || []).concat(actionColumn),
    [listProps, actionColumn]
  );

  /* ------------------------- RENDER ------------------------- */
  const isEditing = editingId !== null;

  return (
    <>
      <Grid item container spacing={2}>
        {children}

        {/* BOTÕES DE ADIÇÃO / ATUALIZAÇÃO */}
        {!readOnlyMode && !hideAddButton && (
          <>
            {!isEditing ? (
              <Grid item xs={6} sm={3} md={3}>
                <QCXButton
                  color="secondary"
                  onClick={handleAddVinculo}
                  disabled={disableAdd || isBusy}
                  fullWidth
                  startIcon={<AddCircleIcon />}
                >
                  {t('com.muralis.qcx.acoes.adicionar')}
                </QCXButton>
              </Grid>
            ) : (
              <>
                <Grid item xs={6} sm={3} md={3}>
                  <QCXButton
                    color="secondary"
                    onClick={handleUpdateVinculo}
                    disabled={isBusy}
                    fullWidth
                    startIcon={<SaveIcon />}
                  >
                    {t('com.muralis.qcx.acoes.atualizar')}
                  </QCXButton>
                </Grid>
                <Grid item xs={6} sm={2} md={2}>
                  <QCXButton
                    variant="outlined"
                    onClick={handleClearCurrentVinculo}
                    fullWidth
                  >
                    <ClearIcon />
                  </QCXButton>
                </Grid>
              </>
            )}
          </>
        )}

        {/* BOTÕES DE EXCLUSÃO EM LOTE */}
        {!readOnlyMode && !disableRemove && (
          <Grid item xs={3}>
            {batchDeleteMode ? (
              <>
                <QCXButton
                  color="secondary"
                  onClick={handleConfirmBatchDelete}
                  disabled={!selectedIds.length || isBusy}
                  fullWidth
                >
                  CONFIRMAR EXCLUSÃO
                </QCXButton>
                <QCXButton
                  variant="outlined"
                  onClick={() => {
                    setBatchDeleteMode(false);
                    setSelectedIds([]);
                  }}
                  fullWidth
                >
                  CANCELAR
                </QCXButton>
              </>
            ) : (
              <QCXButton
                color="primary"
                onClick={() => setBatchDeleteMode(true)}
                disabled={!currentVinculos.length}
                fullWidth
              >
                EXCLUIR EM LOTE
              </QCXButton>
            )}
          </Grid>
        )}

        {/* DATAGRID */}
        <Grid item xs={12}>
          <QCXDataGrid
            rows={currentVinculos}
            columns={columns}
            checkboxSelection={batchDeleteMode}
            onSelectionModelChange={(sel) => setSelectedIds(sel.selectionModel || sel)}
            divProps={{ className: classes.dataGridDiv }}
          />
        </Grid>
      </Grid>

      <QCXConfirmDialog />
    </>
  );
}
